import React from 'react';
import {
  Dialog,
  LinearProgress,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Divider,
  Paper,
  IconButton,
  Avatar,
  makeStyles,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@material-ui/core';
import moment from 'moment';
import { Close } from '@material-ui/icons';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  avatar: {
    width: theme.spacing(5),
    height: theme.spacing(5)
  },
  headerTextMargin: {
    fontWeight: 500
  },

  tableContainer: {
    margin: '0 auto',
    // marginTop: theme.spacing(2),
    maxWidth: 1700,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    overflowX: 'auto'
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  },
  headerCell: {
    color: theme.palette.common.white,
    fontWeight: 'bold',
    textAlign: 'center',
    padding: theme.spacing(2.5)
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    }
  },
  tableCell: {
    padding: theme.spacing(2.5),
    // textAlign: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  title: {
    padding: theme.spacing(2),
    fontWeight: 'bold',
    textAlign: 'center',
    color: theme.palette.primary.main
  }
}));

/**
 * @typedef {{date:string,ixUser:number,items:Array<[string, [string, string]]>}} log
 */

/**
 * @param {object} props
 * @param {{key:number,value:log}} props.log
 */
const Card = ({ log }) => {
  const classes = useStyles();
  const { users = [] } = useSelector(state => state.bizMeta);
  const user = users.find(user => user.ixUser === log.value.ixUser);

  return (
    <Box
      component={Paper}
      p={1}
      display="flex"
      flexDirection="column"
      gridGap={1}
    >
      <Box display="flex" gridGap={2} alignItems="center">
        <Avatar
          className={classes.avatar}
          src={user ? `/api/images/user/${user.username}/avatar.jpg` : null}
        >
          {user ? user.FName[0].toUpperCase() : null}
        </Avatar>
        <Box display="flex" flexDirection="column" ml={2}>
          {Boolean(user) && (
            <Typography variant="caption" className={classes.headerTextMargin}>
              {user.FName + ' ' + user.LName}
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary">
            {moment(log.date).format('MMM DD, YYYY h:mm a')}
          </Typography>
        </Box>
      </Box>
      <Box py={1}>
        <Divider />
      </Box>
      {log.value.items.map(logItem => (
        <Box key={logItem[0]} mt={1}>
          <Typography variant="caption" color="textSecondary">
            {logItem[0]}
          </Typography>
          <Typography variant="body2">
            {`${logItem[1][0]} -> ${logItem[1][1]}`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * @param {object} props
 * @param {Array<log>} props.data
 * @param {boolean} open
 * @param {boolean} isLoading
 * @param {VoidFunction} onClose
 */
const TabularLogs = ({
  data = [],
  settings = {},
  open = false,
  isLoading = false,
  onClose = () => {}
}) => {
  const classes = useStyles();
  const { flds, kvs_flds } = settings?.tabular_logs || {};

  const allowedFields = (kvs_flds || []).concat(flds || []);

  const fieldDescriptions = {
    contactNo: 'Contact Number',
    mname: 'Middle Name',
    'pr.monthlySalary': 'Monthly Salary',
    monthlySalary: 'Monthly Salary',
    'pr.Rate1': 'Rate 1',
    'pr.Rate2': 'Rate 2',
    Rate1: 'Rate 1',
    Rate2: 'Rate 2',
    philhealthNo: 'PhilHealth Number',
    'pr.position': 'Postion',
    addressStreet: 'addressStreet',
    ...settings?.pretty_flds
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Tabular Logs</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      {isLoading && <LinearProgress />}
      <DialogContent style={{ padding: 16 }}>
        {/* <Box display="flex" flexDirection="column" gridGap={2}>
          {data.map(dataItem => (
            <Card key={dataItem.date} log={dataItem} />
          ))}
        </Box> */}
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow className={classes.tableHeader}>
                <TableCell className={classes.headerCell}>Date</TableCell>
                {allowedFields.map(field => (
                  <TableCell key={field} className={classes.headerCell}>
                    {fieldDescriptions[field] || field}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(data || [])
                .sort((a, b) => new Date(b.value.date) - new Date(a.value.date))
                .map(entry => (
                  <TableRow key={entry.key} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>
                      {moment(entry.value.date).format('MMM DD, YYYY hh:mm a')}
                    </TableCell>
                    {(allowedFields || []).map(field => {
                      const item = entry.value.items.find(([f]) => f === field);
                      const values = item ? item[1] : ['-'];
                      const displayValue =
                        values.length > 1 && values[0] !== values[1]
                          ? `${values[0]} → ${values[1]}`
                          : values[0];
                      return (
                        <TableCell key={field} className={classes.tableCell}>
                          {displayValue}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default TabularLogs;
